// src/pages/subscriptions/PricingPage.tsx
// Web port of mobile PricingScreen.tsx — full feature parity.
// Plans, bundle pricing, unit picker, M-Pesa STK push, gift code trial, polling.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check, X, Loader2, Phone, Star, Shield, Zap, Users,
  Building2, Briefcase, Gift, ChevronRight, AlertTriangle,
  CheckCircle, XCircle, Info
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  fetchSubscriptionPlans,
  fetchMySubscriptions,
  fetchTrialStatus,
  sendStkPush,
  fetchPaymentStatus,
  activateTrial,
  LivePlan,
  UserSubscription,
  PortalType,
} from '@/api/subscriptions';

// ─── Static plan definitions (mirrors mobile PROPERTY_PLANS / BUSINESS_PLANS) ──

interface StaticPlan {
  key: string;
  name: string;
  price: number;
  tagline: string;
  maxUnits: string;
  unitsPerProperty?: string;
  aiTokens?: string;
  maxStaff?: string;
  highlight?: boolean;
  verifiedTier: boolean;
  features: { label: string; included: boolean }[];
}

const PROPERTY_PLANS: StaticPlan[] = [
  {
    key: 'mwananchi-property', name: 'Mwananchi', price: 299,
    tagline: 'Start managing your rental property today',
    maxUnits: '1 property', unitsPerProperty: '10 units / 10 tenants', maxStaff: '1 staff',
    verifiedTier: false,
    features: [
      { label: 'Automatic rent collection (M-Pesa)', included: true },
      { label: 'Tenant tracking', included: true },
      { label: 'Rent invoicing', included: true },
      { label: 'Payment reminders (SMS)', included: true },
      { label: 'Vacancy tracking', included: true },
      { label: 'Verified landlord badge', included: false },
      { label: 'Post vacancies publicly', included: false },
      { label: 'Digital leases', included: false },
      { label: 'Bank-ready statements', included: false },
      { label: 'Staff portal', included: false },
    ],
  },
  {
    key: 'mtaa-property', name: 'Mtaa', price: 499, highlight: true,
    tagline: 'Get verified. Post vacancies. Build trust.',
    maxUnits: '1 property', unitsPerProperty: '30 units / 30 tenants',
    aiTokens: '10,000 tokens/mo', maxStaff: '2 staff', verifiedTier: true,
    features: [
      { label: 'Everything in Mwananchi', included: true },
      { label: 'Verified landlord badge (after review)', included: true },
      { label: 'Post vacancies publicly', included: true },
      { label: 'Tenant portal (tenants log in)', included: true },
      { label: 'Staff portal (caretaker can manage)', included: true },
      { label: 'AI assistant (10K tokens)', included: true },
      { label: 'Digital leases', included: false },
      { label: 'Bank-ready rental income statements', included: false },
      { label: 'Credit-readiness scoring', included: false },
    ],
  },
  {
    key: 'biashara-property', name: 'Biashara', price: 999,
    tagline: 'Build your landlord credit profile. Access property loans.',
    maxUnits: '1 property', unitsPerProperty: '100 units / unlimited tenants',
    aiTokens: '30,000 tokens/mo', maxStaff: '5 staff', verifiedTier: true,
    features: [
      { label: 'Everything in Mtaa', included: true },
      { label: 'Digital leases (legally binding, e-signed)', included: true },
      { label: 'Bank-ready rental income statements (6mo)', included: true },
      { label: 'Credit-readiness scoring (for property loans)', included: true },
      { label: 'Tenant screening (AI-powered)', included: true },
      { label: 'Advanced analytics (occupancy, revenue)', included: true },
      { label: 'AI assistant (30K tokens)', included: true },
      { label: 'Priority verification (48hrs vs 7 days)', included: true },
    ],
  },
  {
    key: 'titan-property', name: 'Titan', price: 1499,
    tagline: 'Manage multiple properties. Professional landlord.',
    maxUnits: '5 properties', unitsPerProperty: '500 units total / unlimited tenants',
    aiTokens: '100,000 tokens/mo', maxStaff: '10 staff', verifiedTier: true,
    features: [
      { label: 'Everything in Biashara', included: true },
      { label: 'Multi-property dashboard (5 properties)', included: true },
      { label: 'Property-level reporting (P&L per property)', included: true },
      { label: 'Bulk tenant operations', included: true },
      { label: 'Maintenance tracking', included: true },
      { label: 'AI assistant (100K tokens)', included: true },
    ],
  },
  {
    key: 'enterprise-property', name: 'Enterprise', price: 3499,
    tagline: 'Full property portfolio. Institutional landlord.',
    maxUnits: '50 properties (hard cap)', unitsPerProperty: '5,000 units total / unlimited tenants',
    aiTokens: '500,000 tokens/mo', maxStaff: '25 staff', verifiedTier: true,
    features: [
      { label: 'Everything in Titan', included: true },
      { label: 'Unlimited locations (nationwide ops)', included: true },
      { label: 'Dedicated Majengo Captain', included: true },
      { label: 'Co-op Bank referral access (priority loans)', included: true },
      { label: 'Custom integrations', included: true },
      { label: 'White-label option', included: true },
      { label: 'Portfolio analytics (IRR, NOI, cap rates)', included: true },
      { label: 'SLA guarantee (99.9% uptime)', included: true },
    ],
  },
];

const BUSINESS_PLANS: StaticPlan[] = [
  {
    key: 'mwananchi-business', name: 'Mwananchi', price: 299,
    tagline: 'Start tracking your inventory today',
    maxUnits: '1 business account', unitsPerProperty: '100 products / 500 inventory items',
    maxStaff: '1 staff', verifiedTier: false,
    features: [
      { label: 'Product management (add/edit)', included: true },
      { label: 'Inventory tracking (stock in/out)', included: true },
      { label: 'Barcode & QR scanning', included: true },
      { label: 'Basic invoicing', included: true },
      { label: 'Sales history (90 days)', included: true },
      { label: 'Verified badge', included: false },
      { label: 'Public product listings', included: false },
      { label: 'Staff management', included: false },
      { label: 'AI assistant (chat, auto-reply, voice)', included: false },
      { label: 'Credit-readiness scoring', included: false },
    ],
  },
  {
    key: 'mtaa-business', name: 'Mtaa', price: 499, highlight: true,
    tagline: 'Get verified. Hire staff. Go public.',
    maxUnits: '1 business account', unitsPerProperty: '500 products / 2,500 inventory items',
    aiTokens: '10,000 tokens/mo', maxStaff: '2 staff', verifiedTier: true,
    features: [
      { label: 'Everything in Mwananchi', included: true },
      { label: 'Verified badge (gold/black badge after review)', included: true },
      { label: 'Post products publicly (customers browse)', included: true },
      { label: 'Staff management (2 accounts)', included: true },
      { label: 'AI chat assistant on website (10K tokens)', included: true },
      { label: 'Sales history (12 months)', included: true },
      { label: 'Export reports (Excel/PDF)', included: true },
      { label: 'AI auto-reply (hands-free responses)', included: false },
      { label: 'AI voice calling', included: false },
      { label: 'Credit-readiness scoring', included: false },
    ],
  },
  {
    key: 'biashara-business', name: 'Biashara', price: 999,
    tagline: 'Build your credit profile. Get access to loans.',
    maxUnits: '1 business account', unitsPerProperty: '1,500 products / 10,000 inventory items',
    aiTokens: '30,000 tokens/mo', maxStaff: '5 staff', verifiedTier: true,
    features: [
      { label: 'Everything in Mtaa', included: true },
      { label: 'AI auto-reply — AI handles customer messages automatically', included: true },
      { label: 'SKU analytics (profitability, turnover, bestsellers)', included: true },
      { label: 'Credit-readiness scoring (updated monthly)', included: true },
      { label: 'Bank-ready statements (6-month exportable)', included: true },
      { label: 'Advanced AI insights (30K tokens)', included: true },
      { label: 'Priority verification (48 hours vs 7 days)', included: true },
      { label: 'AI voice calling', included: false },
    ],
  },
  {
    key: 'titan-business', name: 'Titan', price: 1499,
    tagline: 'Manage multiple locations. Scale operations.',
    maxUnits: '1 business account', unitsPerProperty: '5,000 products / 50,000 inventory items',
    aiTokens: '100,000 tokens/mo', maxStaff: '10 staff', verifiedTier: true,
    features: [
      { label: 'Everything in Biashara', included: true },
      { label: 'AI voice calling (customers call, AI answers)', included: true },
      { label: 'Multi-location inventory (3-5 locations)', included: true },
      { label: 'Location-level reporting (P&L per location)', included: true },
      { label: 'Bulk operations (import/export CSV)', included: true },
      { label: 'API access (integrate external systems)', included: true },
      { label: 'Dedicated support (priority email + phone)', included: true },
    ],
  },
  {
    key: 'enterprise-business', name: 'Enterprise', price: 3499,
    tagline: 'Full platform. Dedicated Captain. Bank partnerships.',
    maxUnits: '1 business account (enterprise-grade)', unitsPerProperty: '20,000 products / 200,000 inventory items',
    aiTokens: '500,000 tokens/mo', maxStaff: '25 staff', verifiedTier: true,
    features: [
      { label: 'Everything in Titan', included: true },
      { label: 'Unlimited locations (nationwide operations)', included: true },
      { label: 'Dedicated Majengo Captain (personal manager)', included: true },
      { label: 'Co-op Bank referral access (priority loans)', included: true },
      { label: 'Custom integrations (we build for you)', included: true },
      { label: 'White-label option (rebrand as your system)', included: true },
      { label: 'Advanced fraud detection', included: true },
      { label: 'SLA guarantee (99.9% uptime)', included: true },
      { label: 'Custom contracts (negotiate terms)', included: true },
    ],
  },
];

// ─── Bundle pricing ─────────────────────────────────────────────────────────────
const BUNDLE_PRICES: Record<string, Record<1 | 2 | 3, { price: number; saving: number }>> = {
  Mwananchi: { 1: { price: 299, saving: 0 },   2: { price: 499, saving: 99 },   3: { price: 699,  saving: 198 } },
  Mtaa:      { 1: { price: 499, saving: 0 },   2: { price: 799, saving: 199 },  3: { price: 1299, saving: 198 } },
  Biashara:  { 1: { price: 999, saving: 0 },   2: { price: 1799, saving: 199 }, 3: { price: 2599, saving: 398 } },
  Titan:     { 1: { price: 1499, saving: 0 },  2: { price: 2499, saving: 499 }, 3: { price: 3999, saving: 498 } },
  Enterprise:{ 1: { price: 3499, saving: 0 },  2: { price: 6499, saving: 499 }, 3: { price: 8999, saving: 1498 } },
};
const getBundlePrice = (name: string, units: 1 | 2 | 3) =>
  BUNDLE_PRICES[name]?.[units] ?? { price: 0, saving: 0 };

// ─── Plan rank for upgrade/downgrade detection ───────────────────────────────
const PLAN_RANK: Record<string, number> = {
  Mwananchi: 1, Mtaa: 2, Biashara: 3, Titan: 4, Enterprise: 5,
};

// ─── Plan brand colours ──────────────────────────────────────────────────────
const PLAN_COLORS: Record<string, string> = {
  Mwananchi: '#10b981',
  Mtaa:      '#6366f1',
  Biashara:  '#f59e0b',
  Titan:     '#ef4444',
  Enterprise:'#8b5cf6',
};

// ─── Payment step type ────────────────────────────────────────────────────────
type PayStep = 'idle' | 'phone_entry' | 'waiting' | 'success' | 'failed';

// ─── Component ────────────────────────────────────────────────────────────────
const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<PortalType>('property');
  const [unitCount, setUnitCount] = useState<1 | 2 | 3>(1);
  const [livePlans, setLivePlans] = useState<{ property: LivePlan[]; business: LivePlan[] }>({ property: [], business: [] });
  const [userSubs, setUserSubs] = useState<{ property: UserSubscription | null; business: UserSubscription | null }>({ property: null, business: null });
  const [trialStatus, setTrialStatus] = useState<{ property: boolean; business: boolean }>({ property: false, business: false });
  const [fetchingLive, setFetchingLive] = useState(true);

  // Payment flow
  const [selectedPlan, setSelectedPlan] = useState<StaticPlan | null>(null);
  const [payStep, setPayStep] = useState<PayStep>('idle');
  const [phone, setPhone] = useState('254');
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [pollSeconds, setPollSeconds] = useState(0);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Gift code / trial
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftCode, setGiftCode] = useState('');
  const [giftLoading, setGiftLoading] = useState(false);
  const [pendingTrialPlan, setPendingTrialPlan] = useState<StaticPlan | null>(null);

  // Success celebration
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState<any>(null);

  // Upgrade confirm
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
  const [upgradePlan, setUpgradePlan] = useState<StaticPlan | null>(null);

  // ─── Fetch live data ────────────────────────────────────────────────────────
  const fetchLiveData = useCallback(async () => {
    try {
      const [plansRes, subsRes, propTrialRes, bizTrialRes] = await Promise.all([
        fetchSubscriptionPlans(),
        user ? fetchMySubscriptions() : Promise.resolve({ property: null, business: null }),
        user ? fetchTrialStatus('property') : Promise.resolve({ trialUsed: false }),
        user ? fetchTrialStatus('business') : Promise.resolve({ trialUsed: false }),
      ]);
      setLivePlans(plansRes);
      setUserSubs(subsRes);
      setTrialStatus({ property: propTrialRes.trialUsed, business: bizTrialRes.trialUsed });
    } catch {
      /* static plan data still renders */
    } finally {
      setFetchingLive(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLiveData();
    return () => stopPolling();
  }, [fetchLiveData]);

  // ─── Derived values ─────────────────────────────────────────────────────────
  const plans = activeTab === 'property' ? PROPERTY_PLANS : BUSINESS_PLANS;
  const currentSub = userSubs[activeTab];
  const currentPlanName: string | null =
    currentSub?.effective_plan?.name ?? currentSub?.subscription_plans?.name ?? null;

  const getLivePlanId = (plan: StaticPlan): string | null =>
    (livePlans[activeTab] ?? []).find(p => p.name === plan.name)?.id ?? null;

  const isCurrentPlan = (plan: StaticPlan) => currentPlanName === plan.name;

  // ─── Subscribe handler ──────────────────────────────────────────────────────
  const handleSubscribe = (plan: StaticPlan) => {
    if (!user) {
      toast.warning('Sign in required', 'Please sign in to subscribe.');
      navigate('/login');
      return;
    }
    if (!getLivePlanId(plan)) {
      toast.info('Coming soon', 'Plans are being configured. Please check back shortly.');
      return;
    }

    if (currentSub && currentPlanName && currentPlanName !== plan.name) {
      const currentRank = PLAN_RANK[currentPlanName] ?? 0;
      const targetRank = PLAN_RANK[plan.name] ?? 0;
      if (targetRank < currentRank) {
        const expiryDate = currentSub.current_period_end
          ? new Date(currentSub.current_period_end).toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' })
          : null;
        toast.warning(
          'Downgrade Not Allowed',
          `You're on ${currentPlanName}${expiryDate ? `, active until ${expiryDate}` : ''}. You can subscribe to ${plan.name} after your current plan expires.`
        );
        return;
      }
      // Upgrade path — show confirm dialog
      setUpgradePlan(plan);
      setShowUpgradeConfirm(true);
      return;
    }

    setSelectedPlan(plan);
    setPhone('254');
    setPayStep('phone_entry');
  };

  // ─── Trial / gift code ──────────────────────────────────────────────────────
  const openGiftModal = (plan: StaticPlan) => {
    setPendingTrialPlan(plan);
    setGiftCode('');
    setShowGiftModal(true);
  };

  const handleActivateTrial = async () => {
    if (!giftCode.trim()) {
      toast.warning('Required', 'Please enter a gift code.');
      return;
    }
    setGiftLoading(true);
    try {
      const data = await activateTrial(activeTab, giftCode.trim().toUpperCase());
      setShowGiftModal(false);
      if (data.success) {
        await fetchLiveData();
        setCelebrationData({
          portalType: activeTab,
          portalName: activeTab === 'property' ? 'Property Management' : 'Business Management',
          trialEnd: data.trial_end,
          role: data.role_assigned,
          trialDays: data.trial_days ?? 7,
        });
        setShowCelebration(true);
      } else {
        toast.error('Trial Unavailable', data.message || 'Invalid gift code or trial already used.');
      }
    } catch (e: any) {
      toast.error('Error', e?.response?.data?.error || 'Failed to activate trial.');
    } finally {
      setGiftLoading(false);
    }
  };

  // ─── STK push ───────────────────────────────────────────────────────────────
  const handleSendStkPush = async () => {
    if (!selectedPlan || !user) return;

    let formatted = phone.replace(/\s/g, '').replace('+', '');
    if (formatted.startsWith('0')) formatted = '254' + formatted.substring(1);
    if (!formatted.startsWith('254') || formatted.length !== 12) {
      toast.warning('Invalid number', 'Enter a valid M-Pesa number, e.g. 0712 345 678');
      return;
    }

    const liveList = livePlans[activeTab] ?? [];
    const livePlan = liveList.find(p => p.name === selectedPlan.name);
    const planId = livePlan?.id;
    if (!planId) return;

    const { price: bundlePrice } = getBundlePrice(selectedPlan.name, unitCount);
    const amountToCharge = bundlePrice || livePlan?.price_monthly || selectedPlan.price;

    setPayStep('waiting');
    setPollSeconds(0);

    try {
      const data = await sendStkPush({
        phoneNumber: formatted,
        amount: amountToCharge,
        planId,
        portal_type: activeTab,
        userId: user.id,
        unit_count: unitCount,
        planName: `${selectedPlan.name} ${activeTab === 'property' ? 'Property' : 'Business'} x${unitCount}`,
      });

      const cid = data.checkout_request_id || data.CheckoutRequestID;
      if (!cid) throw new Error('No CheckoutRequestID returned');
      setCheckoutId(cid);
      startPolling(cid);
    } catch (e: any) {
      setPayStep('failed');
      toast.error('STK Push Failed', e?.response?.data?.error || 'Could not send M-Pesa request.');
    }
  };

  // ─── Polling ─────────────────────────────────────────────────────────────────
  const startPolling = (cid: string) => {
    stopPolling();
    let elapsed = 0;
    pollTimer.current = setInterval(async () => {
      elapsed += 3;
      setPollSeconds(elapsed);
      if (elapsed >= 90) {
        stopPolling();
        setPayStep('failed');
        toast.warning('Timed out', "We didn't receive a payment confirmation. If you paid, contact support.");
        return;
      }
      try {
        const data = await fetchPaymentStatus(cid);
        if (data.status === 'activated' || data.status === 'paid') {
          stopPolling();
          setPayStep('success');
          await fetchLiveData();
        } else if (data.status === 'failed') {
          stopPolling();
          setPayStep('failed');
          toast.error('Payment declined', data.result_desc || 'M-Pesa payment was not completed.');
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const stopPolling = () => {
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
  };

  const dismissModal = () => {
    stopPolling();
    setPayStep('idle');
    setSelectedPlan(null);
    setCheckoutId(null);
    setPollSeconds(0);
  };

  const navigateAfterSuccess = () => {
    dismissModal();
    if (activeTab === 'property') navigate('/landlord');
    else navigate('/business');
  };

  // ─── Unit picker labels ──────────────────────────────────────────────────────
  const unitLabels: { val: 1 | 2 | 3; label: string }[] = activeTab === 'property'
    ? [{ val: 1, label: '1 Property' }, { val: 2, label: '2 Properties' }, { val: 3, label: '3+ Properties' }]
    : [{ val: 1, label: '1 Business' }, { val: 2, label: '2 Businesses' }, { val: 3, label: '3+ Businesses' }];

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-20">
      {/* Header */}
      <div className="text-center pt-14 pb-10 px-4">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
          <Zap size={14} /> Simple, transparent pricing
        </div>
        <h1 className="text-4xl sm:text-5xl font-black text-light-text dark:text-dark-text mb-4 tracking-tight">
          Plans for every
          <span className="text-primary"> Kenyan business</span>
        </h1>
        <p className="text-light-subtle dark:text-dark-subtle text-lg max-w-2xl mx-auto">
          Start free for 7 days. No credit card required. Cancel anytime.
        </p>
      </div>

      {/* Tab Toggle */}
      <div className="flex justify-center mb-10 px-4">
        <div className="flex p-1 rounded-2xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border shadow-sm">
          {(['property', 'business'] as PortalType[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all ${
                activeTab === tab
                  ? 'bg-primary text-white shadow-md'
                  : 'text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text'
              }`}
            >
              {tab === 'property' ? <Building2 size={16} /> : <Briefcase size={16} />}
              {tab === 'property' ? 'Property Management' : 'Business Management'}
            </button>
          ))}
        </div>
      </div>

      {/* Active subscription banner */}
      {currentSub && (
        <div className="max-w-4xl mx-auto px-4 mb-6">
          <div className="flex items-center gap-3 p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700">
            <CheckCircle size={18} className="text-emerald-500 shrink-0" />
            <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
              Active {activeTab} plan: <strong>{currentPlanName}</strong>
              {currentSub.unit_count ? ` · ${currentSub.unit_count === 1 ? '1' : currentSub.unit_count === 2 ? '2' : '3+'} ${activeTab === 'property' ? 'propert' : 'business'}${currentSub.unit_count > 1 ? (activeTab === 'property' ? 'ies' : 'es') : (activeTab === 'property' ? 'y' : '')}` : ''}
            </p>
          </div>
        </div>
      )}

      {/* Unit picker */}
      <div className="max-w-4xl mx-auto px-4 mb-10">
        <div className="p-5 rounded-2xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border">
          <p className="font-bold text-light-text dark:text-dark-text mb-1">
            How many {activeTab === 'property' ? 'properties' : 'businesses'}?
          </p>
          <p className="text-sm text-light-subtle dark:text-dark-subtle mb-4">
            Bundle discounts applied automatically
          </p>
          <div className="grid grid-cols-3 gap-3">
            {unitLabels.map(({ val, label }) => {
              const active = unitCount === val;
              const { saving } = getBundlePrice(plans[1].name, val); // show savings for recommended plan
              return (
                <button
                  key={val}
                  onClick={() => setUnitCount(val)}
                  className={`relative px-4 py-3 rounded-xl text-sm font-bold border-2 transition-all ${
                    active
                      ? 'border-primary bg-primary text-white shadow-lg shadow-primary/20'
                      : 'border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text hover:border-primary/50'
                  }`}
                >
                  {label}
                  {saving > 0 && (
                    <span className={`block text-xs mt-0.5 font-semibold ${active ? 'text-white/80' : 'text-emerald-500'}`}>
                      Save {saving.toLocaleString()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Plan cards */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {plans.map((plan, idx) => {
            const isCurrent = isCurrentPlan(plan);
            const pColor = PLAN_COLORS[plan.name] ?? '#6366f1';
            const { price: displayPrice, saving } = getBundlePrice(plan.name, unitCount);
            const currentRank = PLAN_RANK[currentPlanName ?? ''] ?? 0;
            const thisRank = PLAN_RANK[plan.name] ?? 0;
            const isDowngrade = !!currentSub && !isCurrent && thisRank < currentRank;
            const expiryDate = isDowngrade && currentSub?.current_period_end
              ? new Date(currentSub.current_period_end).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })
              : null;
            const isRecommended = plan.highlight;

            return (
              <div
                key={plan.key}
                className={`relative flex flex-col rounded-3xl p-6 transition-all ${
                  isCurrent
                    ? 'bg-light-card dark:bg-dark-card border-2 border-emerald-500 shadow-lg'
                    : isRecommended
                    ? 'bg-light-card dark:bg-dark-card border-2 shadow-xl scale-[1.02]'
                    : 'bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border opacity-90 hover:opacity-100'
                }`}
                style={isRecommended ? { borderColor: pColor } : undefined}
              >
                {/* Popular badge */}
                {isRecommended && !isCurrent && (
                  <div
                    className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-xs font-bold whitespace-nowrap"
                    style={{ backgroundColor: pColor }}
                  >
                    <Star size={11} fill="white" /> Most Popular
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold whitespace-nowrap">
                    <Check size={11} /> Current Plan
                  </div>
                )}

                {/* Plan name & colour dot */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: pColor }} />
                  <span className="font-black text-lg text-light-text dark:text-dark-text" style={isRecommended ? { color: pColor } : undefined}>
                    {plan.name}
                  </span>
                  {plan.verifiedTier && (
                    <span className="ml-auto flex items-center gap-1 text-[10px] font-bold text-amber-500 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                      <Shield size={9} /> Verified
                    </span>
                  )}
                </div>

                {/* Price */}
                <div className="mb-1">
                  <span className="text-3xl font-black text-light-text dark:text-dark-text" style={isRecommended ? { color: pColor } : undefined}>
                    KES {displayPrice.toLocaleString()}
                  </span>
                  <span className="text-sm text-light-subtle dark:text-dark-subtle font-medium">/mo</span>
                </div>
                {saving > 0 && (
                  <p className="text-xs font-bold text-emerald-500 mb-1">Save KES {saving.toLocaleString()}</p>
                )}
                <p className="text-xs text-light-subtle dark:text-dark-subtle mb-4 leading-relaxed">{plan.tagline}</p>

                {/* Chips */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {[
                    plan.unitsPerProperty,
                    plan.aiTokens && `⚡ ${plan.aiTokens}`,
                    plan.maxStaff && `👥 ${plan.maxStaff}`,
                  ].filter(Boolean).map((chip, i) => (
                    <span key={i} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle">
                      {chip}
                    </span>
                  ))}
                </div>

                <div className="w-full h-px bg-light-border dark:border-dark-border mb-4" />

                {/* Feature list */}
                <ul className="space-y-2 flex-1 mb-6">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs">
                      {f.included
                        ? <Check size={13} className="text-emerald-500 shrink-0 mt-0.5" />
                        : <X size={13} className="text-light-border dark:text-dark-border shrink-0 mt-0.5" />}
                      <span className={f.included ? 'text-light-text dark:text-dark-text' : 'text-light-subtle dark:text-dark-subtle'}>
                        {f.label}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA buttons */}
                {plan.name === 'Mwananchi' && !currentSub && !trialStatus[activeTab] ? (
                  <div className="space-y-2">
                    {unitCount === 1 && (
                      <button
                        onClick={() => openGiftModal(plan)}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold transition-all"
                      >
                        <Gift size={14} /> Activate Gift Code
                      </button>
                    )}
                    <button
                      onClick={() => handleSubscribe(plan)}
                      className="w-full py-3 rounded-xl text-sm font-bold border-2 border-light-border dark:border-dark-border hover:border-primary text-primary transition-all"
                    >
                      Subscribe — KES {displayPrice.toLocaleString()}/mo
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => !isCurrent && handleSubscribe(plan)}
                    disabled={isCurrent}
                    className={`w-full py-3 rounded-xl text-sm font-bold transition-all ${
                      isCurrent
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 border-2 border-emerald-300 cursor-default'
                        : isDowngrade
                        ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 border-2 border-amber-300 cursor-not-allowed'
                        : isRecommended
                        ? 'text-white shadow-lg hover:opacity-90'
                        : 'border-2 border-light-border dark:border-dark-border hover:border-primary text-primary'
                    }`}
                    style={isRecommended && !isCurrent && !isDowngrade ? { backgroundColor: pColor } : undefined}
                  >
                    {isCurrent ? '✓ Current Plan'
                      : isDowngrade ? `🔒 Available after ${expiryDate ?? 'expiry'}`
                      : `Subscribe — KES ${displayPrice.toLocaleString()}/mo`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Payment bottom sheet ──────────────────────────────────────────────── */}
      {payStep !== 'idle' && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={payStep === 'waiting' ? undefined : dismissModal} />
          <div className="relative w-full sm:max-w-md bg-light-card dark:bg-dark-card rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">

            {/* Phone entry */}
            {payStep === 'phone_entry' && (
              <>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-black text-light-text dark:text-dark-text">
                      {selectedPlan?.name} · {activeTab === 'property' ? 'Property' : 'Business'}
                      {unitCount > 1 ? ` x${unitCount}` : ''}
                    </h2>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">
                      KES {getBundlePrice(selectedPlan?.name ?? '', unitCount).price.toLocaleString()}/month
                    </p>
                  </div>
                  <button onClick={dismissModal} className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition">
                    <X size={20} className="text-light-subtle dark:text-dark-subtle" />
                  </button>
                </div>
                <label className="block text-sm font-bold text-light-text dark:text-dark-text mb-2">M-Pesa number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="e.g. 0712 345 678"
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-light-text dark:text-dark-text text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-primary/30 mb-4"
                />
                <div className="flex items-start gap-2 p-3 rounded-xl bg-primary/5 border border-primary/20 mb-6">
                  <Info size={15} className="text-primary shrink-0 mt-0.5" />
                  <p className="text-xs text-primary leading-relaxed">
                    You'll receive an M-Pesa prompt on this number. Complete the payment on your phone to activate your plan.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button onClick={dismissModal} className="flex-1 py-3 rounded-xl border-2 border-light-border dark:border-dark-border text-sm font-bold text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition">
                    Cancel
                  </button>
                  <button
                    onClick={handleSendStkPush}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-white text-sm font-bold hover:opacity-90 transition"
                  >
                    <Phone size={15} /> Send M-Pesa Request
                  </button>
                </div>
              </>
            )}

            {/* Waiting */}
            {payStep === 'waiting' && (
              <div className="flex flex-col items-center text-center py-8">
                <Loader2 size={48} className="animate-spin text-primary mb-6" />
                <h2 className="text-xl font-black text-light-text dark:text-dark-text mb-2">Waiting for payment…</h2>
                <p className="text-sm text-light-subtle dark:text-dark-subtle mb-4">
                  Check your phone and enter your M-Pesa PIN to complete the payment.
                </p>
                <p className="text-xs text-light-subtle dark:text-dark-subtle mb-6">
                  {pollSeconds}s · Auto-confirming when Safaricom responds
                </p>
                <button onClick={dismissModal} className="px-6 py-2.5 rounded-xl border-2 border-light-border dark:border-dark-border text-sm font-bold text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-dark-bg transition">
                  Cancel
                </button>
              </div>
            )}

            {/* Success */}
            {payStep === 'success' && (
              <div className="flex flex-col items-center text-center py-8">
                <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-6">
                  <CheckCircle size={40} className="text-emerald-500" />
                </div>
                <h2 className="text-2xl font-black text-light-text dark:text-dark-text mb-2">🎉 Subscribed!</h2>
                <p className="text-sm text-light-subtle dark:text-dark-subtle mb-1">
                  Your <strong>{selectedPlan?.name}</strong> {activeTab === 'property' ? 'Property' : 'Business'} plan is now active.
                </p>
                {selectedPlan?.verifiedTier && (
                  <p className="text-xs text-light-subtle dark:text-dark-subtle mb-6">
                    The Majengo team will verify your account shortly.
                  </p>
                )}
                <button
                  onClick={navigateAfterSuccess}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold transition"
                >
                  Go to Dashboard <ChevronRight size={16} />
                </button>
              </div>
            )}

            {/* Failed */}
            {payStep === 'failed' && (
              <div className="flex flex-col items-center text-center py-8">
                <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6">
                  <XCircle size={40} className="text-red-500" />
                </div>
                <h2 className="text-2xl font-black text-light-text dark:text-dark-text mb-2">Payment failed</h2>
                <p className="text-sm text-light-subtle dark:text-dark-subtle mb-6">
                  The M-Pesa request was not confirmed. Please try again.
                </p>
                <div className="flex gap-3 w-full">
                  <button onClick={dismissModal} className="flex-1 py-3 rounded-xl border-2 border-light-border dark:border-dark-border text-sm font-bold text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition">
                    Cancel
                  </button>
                  <button
                    onClick={() => { setPayStep('phone_entry'); setPhone('254'); }}
                    className="flex-1 py-3 rounded-xl bg-primary text-white text-sm font-bold hover:opacity-90 transition"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Upgrade confirm dialog ───────────────────────────────────────────── */}
      {showUpgradeConfirm && upgradePlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowUpgradeConfirm(false)} />
          <div className="relative w-full max-w-sm bg-light-card dark:bg-dark-card rounded-3xl p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={24} className="text-amber-500" />
              <h2 className="text-lg font-black text-light-text dark:text-dark-text">Confirm Upgrade</h2>
            </div>
            <p className="text-sm text-light-subtle dark:text-dark-subtle mb-6 leading-relaxed">
              Upgrading will immediately switch you to the <strong className="text-light-text dark:text-dark-text">{upgradePlan.name}</strong> tier. The remaining value of your current plan will be automatically converted into extra days on your new cycle.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowUpgradeConfirm(false)} className="flex-1 py-3 rounded-xl border-2 border-light-border dark:border-dark-border text-sm font-bold text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition">
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUpgradeConfirm(false);
                  setSelectedPlan(upgradePlan);
                  setPhone('254');
                  setPayStep('phone_entry');
                }}
                className="flex-1 py-3 rounded-xl bg-primary text-white text-sm font-bold hover:opacity-90 transition"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Gift code modal ──────────────────────────────────────────────────── */}
      {showGiftModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowGiftModal(false)} />
          <div className="relative w-full max-w-sm bg-light-card dark:bg-dark-card rounded-3xl p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-2">
              <Gift size={24} className="text-emerald-500" />
              <h2 className="text-xl font-black text-light-text dark:text-dark-text">Activate Trial</h2>
            </div>
            <p className="text-sm text-light-subtle dark:text-dark-subtle mb-5">
              Enter your gift code to unlock a 7-day free trial of the Mwananchi plan.
            </p>
            <input
              type="text"
              value={giftCode}
              onChange={e => setGiftCode(e.target.value.toUpperCase())}
              placeholder="GIFT-CODE"
              autoFocus
              className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-light-text dark:text-dark-text text-lg font-bold tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-primary/30 mb-5"
            />
            <div className="flex gap-3">
              <button onClick={() => setShowGiftModal(false)} disabled={giftLoading} className="flex-1 py-3 rounded-xl border-2 border-light-border dark:border-dark-border text-sm font-bold text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition disabled:opacity-50">
                Cancel
              </button>
              <button
                onClick={handleActivateTrial}
                disabled={giftLoading || !giftCode.trim()}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold transition disabled:opacity-50"
              >
                {giftLoading ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />}
                {giftLoading ? 'Activating…' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Trial celebration overlay ─────────────────────────────────────────── */}
      {showCelebration && celebrationData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-sm bg-light-card dark:bg-dark-card rounded-3xl p-8 shadow-2xl text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-black text-light-text dark:text-dark-text mb-2">
              Trial Activated!
            </h2>
            <p className="text-sm text-light-subtle dark:text-dark-subtle mb-2">
              You have <strong className="text-light-text dark:text-dark-text">{celebrationData.trialDays} days</strong> of free access to
            </p>
            <p className="text-base font-black text-primary mb-1">{celebrationData.portalName}</p>
            {celebrationData.trialEnd && (
              <p className="text-xs text-light-subtle dark:text-dark-subtle mb-6">
                Trial ends {new Date(celebrationData.trialEnd).toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}
            <button
              onClick={() => {
                setShowCelebration(false);
                if (celebrationData.portalType === 'property') navigate('/landlord');
                else navigate('/business');
              }}
              className="w-full py-3 rounded-xl bg-primary text-white font-bold hover:opacity-90 transition"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
